import React, { useState, useRef, useEffect } from "react";
import { FiUser, FiMail, FiPhone, FiBriefcase, FiGlobe, FiLinkedin, FiMapPin, FiMap, FiAward } from "react-icons/fi";
import "./TextFieldMapper.css";

const TextFieldMapper = ({ image, lines, parsedData, onDataChange }) => {
  const [selectedField, setSelectedField] = useState(null);
  const [hoveredLine, setHoveredLine] = useState(null);
  const [imageScale, setImageScale] = useState(1);
  const imageRef = useRef(null);

  // Debug logging
  useEffect(() => {
    console.log("TextFieldMapper mounted:", {
      hasImage: !!image,
      linesCount: lines?.length,
      imageLength: image?.length,
      parsedData: parsedData
    });
  }, [image, lines, parsedData]);

  const fields = [
    { key: "name", label: "Name", icon: FiUser },
    { key: "company", label: "Company", icon: FiBriefcase },
    { key: "email", label: "Email", icon: FiMail },
    { key: "phone", label: "Phone", icon: FiPhone },
    { key: "role_title", label: "Title", icon: FiAward },
    { key: "street", label: "Street", icon: FiMapPin },
    { key: "city", label: "City", icon: FiMap },
    { key: "state", label: "State", icon: FiMap },
    { key: "zip", label: "ZIP", icon: FiMapPin },
    { key: "linkedin", label: "LinkedIn", icon: FiLinkedin },
    { key: "domain", label: "Website", icon: FiGlobe },
  ];

  useEffect(() => {
    const updateScale = () => {
      if (imageRef.current) {
        const naturalWidth = imageRef.current.naturalWidth;
        const displayWidth = imageRef.current.width;
        setImageScale(displayWidth / naturalWidth);
      }
    };

    if (imageRef.current && imageRef.current.complete) {
      updateScale();
    }

    window.addEventListener('resize', updateScale);
    return () => window.removeEventListener('resize', updateScale);
  }, [image]);

  const handleImageLoad = () => {
    if (imageRef.current) {
      const naturalWidth = imageRef.current.naturalWidth;
      const displayWidth = imageRef.current.width;
      setImageScale(displayWidth / naturalWidth);
    }
  };

  const handleLineClick = (line) => {
    if (selectedField) {
      onDataChange(selectedField, line.text);
      setSelectedField(null);
    }
  };

  const handleFieldSelect = (fieldKey) => {
    if (selectedField === fieldKey) {
      setSelectedField(null);
    } else {
      setSelectedField(fieldKey);
    }
  };

  const isLineUsed = (lineText) => {
    return Object.values(parsedData).some(value => 
      value && value.toLowerCase() === lineText.toLowerCase()
    );
  };

  const getFieldForLine = (lineText) => {
    for (const [key, value] of Object.entries(parsedData)) {
      if (value && value.toLowerCase() === lineText.toLowerCase()) {
        return fields.find(f => f.key === key);
      }
    }
    return null;
  };

  return (
    <div className="text-field-mapper">
      <div className="mapper-instructions">
        <h4>Interactive Field Mapping</h4>
        <p>
          {selectedField 
            ? `Click on text in the image to assign it to "${fields.find(f => f.key === selectedField)?.label}"` 
            : "Select a field below, then click text on the image to map it"}
        </p>
      </div>

      <div className="mapper-content">
        {/* Image with clickable text overlays */}
        <div className="image-container">
          <img 
            ref={imageRef}
            src={image} 
            alt="Business card" 
            className="business-card-image"
            onLoad={handleImageLoad}
          />
          
          {/* Overlay clickable regions for each line */}
          <div className="text-overlay">
            {lines.map((line, index) => {
              const bbox = line.bbox;
              if (!bbox || typeof bbox.x0 === 'undefined') {
                console.warn(`Line ${index} missing bbox:`, line);
                return null;
              }
              
              const usedField = getFieldForLine(line.text);
              const isUsed = isLineUsed(line.text);
              const isHovered = hoveredLine === index;

              return (
                <div
                  key={index}
                  className={`text-region ${selectedField ? 'selectable' : ''} ${isHovered ? 'hovered' : ''} ${isUsed ? 'used' : ''}`}
                  style={{
                    left: `${bbox.x0 * imageScale}px`,
                    top: `${bbox.y0 * imageScale}px`,
                    width: `${(bbox.x1 - bbox.x0) * imageScale}px`,
                    height: `${(bbox.y1 - bbox.y0) * imageScale}px`,
                  }}
                  onClick={() => handleLineClick(line)}
                  onMouseEnter={() => setHoveredLine(index)}
                  onMouseLeave={() => setHoveredLine(null)}
                  title={line.text}
                >
                  {isUsed && usedField && (
                    <span className="field-badge">
                      {React.createElement(usedField.icon, { size: 14 })}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Field selection panel */}
        <div className="fields-panel">
          <h5>Select Field to Map:</h5>
          <div className="field-buttons">
            {fields.map(field => {
              const IconComponent = field.icon;
              return (
                <button
                  key={field.key}
                  className={`field-button ${selectedField === field.key ? 'selected' : ''} ${parsedData[field.key] ? 'has-value' : ''}`}
                  onClick={() => handleFieldSelect(field.key)}
                >
                  <span className="field-icon">
                    <IconComponent size={16} />
                  </span>
                  <span className="field-label">{field.label}</span>
                  {parsedData[field.key] && (
                    <span className="field-value">{parsedData[field.key]}</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Text lines list (alternative selection method) */}
      <div className="text-lines-list">
        <h5>Or select from extracted text:</h5>
        <div className="lines-container">
          {lines.map((line, index) => {
            const usedField = getFieldForLine(line.text);
            return (
              <div
                key={index}
                className={`text-line-item ${selectedField ? 'selectable' : ''} ${usedField ? 'assigned' : ''}`}
                onClick={() => handleLineClick(line)}
                onMouseEnter={() => setHoveredLine(index)}
                onMouseLeave={() => setHoveredLine(null)}
              >
                <span className="line-text">{line.text}</span>
                {usedField && (
                  <span className="assigned-to">
                    {React.createElement(usedField.icon, { size: 12 })} {usedField.label}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default TextFieldMapper;

